import { useState, useCallback, useRef } from "react";
import { createUserWithEmailAndPassword, UserCredential, sendEmailVerification } from "firebase/auth";

import { auth } from "../config/firebase/firebase";
import { useCreateUserMutation } from "../graphql/mutations/create-user.generated";
import { useGetUserInvitationByEmailLazyQuery } from "../graphql/queries/get-user-invitation-by-email.generated";
import { useAuth } from "context/AuthContext";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import regex from "constants/regex";
import routeNames from "constants/route-names";

interface UseSignUpProps {
	guideId?: string | null;
	onSignUpSuccess?: () => void;
}

// Shown when the account could not be created for a reason the person cannot act on.
// It is deliberately vague about the cause: the detail is in the server logs, and here
// it would only be noise.
const GENERIC_SIGN_UP_ERROR = "We could not create your account. Please try again, or contact support if the problem continues.";

// Firebase reports what went wrong through `error.code`. Only the cases the person can
// actually do something about get their own message.
const firebaseSignUpErrorMessage = (code?: string): string => {
	switch (code) {
		case "auth/email-already-in-use":
			return "There is already an account with this email address. Try signing in instead.";
		case "auth/invalid-email":
			return "Please provide a valid email address.";
		case "auth/weak-password":
			return "The password is too weak. Use at least 6 characters.";
		case "auth/network-request-failed":
			return "We could not reach the server. Check your connection and try again.";
		case "auth/too-many-requests":
			return "Too many attempts. Please wait a moment and try again.";
		default:
			return GENERIC_SIGN_UP_ERROR;
	}
};

export const useSignUp = ({ guideId, onSignUpSuccess }: UseSignUpProps = {}) => {
	const { logout, setLoggedUser, updateToken } = useAuth();
	const { updateLoggedUser } = useLoggedUser();

	const [firstName, setFirstName] = useState<string>("");
	const [lastName, setLastName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
	const [firebaseUser, setFirebaseUser] = useState<any>(null);
	const [signUpError, setSignUpError] = useState<string | null>(null);
	const wasInvitedRef = useRef(false);

	const [checkUserInvitation] = useGetUserInvitationByEmailLazyQuery();

	const handleOnChangePhoneNumber = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { value: inputValue } = event.target;

		if (regex.phoneWithNumbers.test(inputValue) || regex.phoneWithNumbersAndDashes.test(inputValue)) {
			setPhoneNumber(inputValue);
		}
	}, []);

	const handleOnCompleted = useCallback(
		async (data: any) => {
			console.log("handleOnCompleted createUserMutation data: ", data);
			if (data && data.create_user && data.create_user.success) {
				const { createdUser, handover_id } = data.create_user;
				if (createdUser) {
					updateLoggedUser(createdUser);
				}

				if (firebaseUser) {
					if (wasInvitedRef.current) {
						// Reload to pick up the server-side email verification
						await firebaseUser.reload();
					}

					if (setLoggedUser) {
						setLoggedUser(firebaseUser);
					}

					if (updateToken) {
						await updateToken(firebaseUser);
					}

					if (wasInvitedRef.current) {
						let path = routeNames.DASHBOARD;

						if (handover_id) {
							path = routeNames.HANDOVER_DETAILS.replace(":handoverId", handover_id);
						}

						// Navigate directly to dashboard — no email verification needed
						window.dispatchEvent(
							new CustomEvent("authNavigation", {
								detail: { route: path }
							})
						);
						return;
					}
				}

				// Call success callback if provided (for modal to show verification message)
				if (onSignUpSuccess) {
					onSignUpSuccess();
				}
			}
		},
		[updateLoggedUser, setLoggedUser, firebaseUser, updateToken, onSignUpSuccess]
	);

	const handleOnError = useCallback(
		async (error: any) => {
			console.log("createUserMutation error: ", error);
			setSignUpError(GENERIC_SIGN_UP_ERROR);

			if (logout) {
				logout(false);
			}

			// The Firebase account exists but the user row does not, so it is removed to
			// leave nothing half-created: otherwise the next attempt fails on the email
			// already being taken and there is no way out of the form.
			if (firebaseUser) {
				try {
					await firebaseUser.delete();
				} catch (deleteError) {
					console.log("Firebase user delete error: ", deleteError);
				}
			}
		},
		[logout, firebaseUser]
	);

	const [createUserMutation, { loading }] = useCreateUserMutation({
		errorPolicy: "none",
		notifyOnNetworkStatusChange: true,
		onError: handleOnError,
		onCompleted: handleOnCompleted
	});

	const createUser = useCallback(async () => {
		if (email && password && firstName && lastName && termsAccepted) {
			let firebaseNewUser: UserCredential | null = null;
			wasInvitedRef.current = false;
			setSignUpError(null);

			try {
				firebaseNewUser = await createUserWithEmailAndPassword(auth, email, password);

				setFirebaseUser(firebaseNewUser.user);

				// Check if user was invited — invited users are verified server-side
				const invitationResult = await checkUserInvitation({
					variables: { email }
				});
				const hasInvitation = (invitationResult.data?.user_invitations?.length ?? 0) > 0;
				wasInvitedRef.current = hasInvitation;

				if (!hasInvitation) {
					// Build the continue URL that Firebase will redirect to after email verification
					const baseUrl = window.location.origin;

					console.log("baseUrl: ", baseUrl);

					let continueUrlParam = `${baseUrl}/dashboard`;

					// If user came from a guide, redirect them back there
					if (guideId) {
						continueUrlParam = `${baseUrl}/guide/${guideId}`;
					}

					console.log("continueUrlParam: ", continueUrlParam);

					// Send email verification with continue URL
					await sendEmailVerification(firebaseNewUser.user, {
						url: continueUrlParam,
						handleCodeInApp: true
					});
				}
			} catch (error: any) {
				console.log("Firebase createUserWithEmailAndPassword Error: ", error);
				console.log("Firebase createUserWithEmailAndPassword Error.code: ", error.code);
				console.log("Firebase createUserWithEmailAndPassword Error.message: ", error.message);
				setSignUpError(firebaseSignUpErrorMessage(error?.code));
			}

			console.log("firebaseNewUser: ", firebaseNewUser);
			if (firebaseNewUser) {
				const newUser = {
					id: firebaseNewUser.user.uid,
					first_name: firstName,
					last_name: lastName,
					email: email,
					phone_number: phoneNumber ? phoneNumber : null
				};

				console.log("newUser: ", newUser);
				try {
					await createUserMutation({
						variables: newUser
					});
				} catch (error) {
					// handleOnError already reported it; this only catches a rejection
					// that never reached the onError callback, such as a network failure.
					console.log("createUserMutation error. Error: ", error);
					setSignUpError(current => current ?? GENERIC_SIGN_UP_ERROR);
				}
			}
		}
	}, [
		firstName,
		lastName,
		email,
		phoneNumber,
		password,
		termsAccepted,
		guideId,
		createUserMutation,
		checkUserInvitation
	]);

	const resetForm = useCallback(() => {
		setFirstName("");
		setLastName("");
		setEmail("");
		setPhoneNumber("");
		setPassword("");
		setTermsAccepted(false);
		setFirebaseUser(null);
		setSignUpError(null);
	}, []);

	return {
		// State values
		firstName,
		lastName,
		email,
		phoneNumber,
		password,
		termsAccepted,

		// State setters
		setFirstName,
		setLastName,
		setEmail,
		setPhoneNumber,
		setPassword,
		setTermsAccepted,

		// Handlers
		handleOnChangePhoneNumber,
		createUser,
		resetForm,

		// Loading state
		loading,

		// Why the last attempt failed, in words the person can act on
		signUpError
	};
};
